// Structural subsets of ChatLayout's types — only what the sidebar renders.
type DmSpace = { id: string; name?: string | null; avatarUrl?: string | null };
type DmMsg = {
  createdAt?: string;
  authorId?: string | number;
  authorName?: string;
  kind?: string;
  attachments?: unknown[];
  content?: string;
};
type DmFriend = { id: string | number; name?: string; username?: string; avatarUrl?: string | null };

type DmSidebarProps = {
  dmSearch: string;
  dmSearchOpen: boolean;
  setDmSearch: (value: string) => void;
  setDmSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
  friendsOnline: DmFriend[];
  friendsList: DmFriend[];
  dmStartBusyUserId: string | null;
  activeDmVoids: DmSpace[];
  msgsByKey: Record<string, DmMsg[]>;
  unread: Record<string, number>;
  notified: Record<string, boolean>;
  currentVoidId: string;
  meUserId?: string;
  tabBgClass: string;
  getDmDisplayName: (space?: { id?: string; name?: string | null } | null, fallbackId?: string) => string;
  onStartDm: (userId: string) => void | Promise<void>;
  onOpenFriends: () => void;
  onOpenDm: (voidId: string) => void;
};

function DmSidebar({
  dmSearch,
  dmSearchOpen,
  setDmSearch,
  setDmSearchOpen,
  friendsOnline,
  friendsList,
  dmStartBusyUserId,
  activeDmVoids,
  msgsByKey,
  unread,
  notified,
  currentVoidId,
  meUserId,
  tabBgClass,
  getDmDisplayName,
  onStartDm,
  onOpenFriends,
  onOpenDm,
}: DmSidebarProps) {
  return (
    <aside aria-label="Direct messages" className="hidden md:flex flex-col min-h-0 bg-panel-2 border-r border-white/10 shrink-0" style={{ width: 'clamp(13rem, 26vw, 260px)' }}>
      {/* Header */}
      <div className="h-12 flex items-center justify-between gap-2 px-3 border-b border-white/10 shrink-0">
        <span className="text-sm font-medium text-fg">Direct Messages</span>
        <div className="flex items-center gap-1">
          <button
            className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${dmSearchOpen ? 'text-white bg-white/10' : 'text-fg-3 hover:text-white hover:bg-white/8'}`}
            aria-label="Search"
            onClick={() => { setDmSearchOpen(o => !o); setDmSearch(''); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          </button>
          <button
            className="flex h-7 w-7 items-center justify-center rounded-lg text-fg-3 hover:text-white hover:bg-white/8 transition-colors"
            aria-label="Add Friends"
            title="Add Friends"
            onClick={onOpenFriends}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
          </button>
        </div>
      </div>
      {/* Inline search */}
      {dmSearchOpen && (
        <div className="px-2 pt-2 shrink-0">
          <div className="flex items-center gap-2 rounded-lg bg-black/30 border border-white/10 px-2 py-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 text-fg-3 shrink-0"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input autoFocus value={dmSearch} onChange={e => setDmSearch(e.target.value)} placeholder="Search..." aria-label="Search direct messages" className="flex-1 bg-transparent text-xs text-white outline-none placeholder:text-fg-3 min-w-0" />
            <button className="text-fg-3 hover:text-white" onClick={() => { setDmSearch(''); setDmSearchOpen(false); }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Online friends */}
        {!dmSearchOpen && friendsOnline.length > 0 && (
          <div className="px-2 pt-3 pb-1">
            <p className="px-1 mb-2 text-meta text-fg-3 font-medium">Online — {friendsOnline.length}</p>
            <div className="stagger-children flex gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {friendsOnline.map(f => (
                <button key={f.id} className="flex flex-col items-center gap-1 shrink-0" disabled={!!dmStartBusyUserId} onClick={() => { void onStartDm(String(f.id)); }}>
                  <div className="relative h-10 w-10">
                    <div className="h-full w-full rounded-full overflow-hidden bg-white/10 flex items-center justify-center">
                      {f.avatarUrl ? <ThumbImg w={56} src={f.avatarUrl} alt={f.name || ''} className="h-full w-full object-cover" /> : <span className="text-sm font-medium text-fg-2">{((f.name || f.username || '?')[0]).toUpperCase()}</span>}
                    </div>
                    <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-online ring-2 ring-black" />
                  </div>
                  <span className="text-meta text-fg-2 max-w-[44px] truncate leading-tight">{f.name || f.username || ''}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {/* DM list */}
        <div className="stagger-children px-2 py-2 space-y-0.5">
          {(() => {
            const filtered = activeDmVoids.filter(v => !dmSearch.trim() || getDmDisplayName(v, v.id).toLowerCase().includes(dmSearch.toLowerCase()));
            const sorted = [...filtered].sort((a, b) => {
              const aTs = msgsByKey[`${a.id}:chat`]?.at(-1)?.createdAt ? new Date(msgsByKey[`${a.id}:chat`].at(-1)!.createdAt!).getTime() : 0;
              const bTs = msgsByKey[`${b.id}:chat`]?.at(-1)?.createdAt ? new Date(msgsByKey[`${b.id}:chat`].at(-1)!.createdAt!).getTime() : 0;
              if (aTs && bTs) return bTs - aTs;
              if (aTs) return -1; if (bTs) return 1;
              return getDmDisplayName(a, a.id).localeCompare(getDmDisplayName(b, b.id));
            });
            if (sorted.length === 0) return (
              <div className="flex flex-col items-center justify-center py-10 text-fg-3 gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 opacity-40"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/></svg>
                <p className="text-xs">{dmSearch ? 'No matches' : 'No messages yet'}</p>
              </div>
            );
            return sorted.map(v => {
              const key = `${v.id}:chat`;
              const lastMsg = msgsByKey[key]?.at(-1);
              const unreadCount = unread[key] || 0;
              const hasNotif = !!notified[key];
              const displayName = getDmDisplayName(v, v.id);
              const isActive = currentVoidId === v.id;
              const peerId = v.id.replace(/^dm_/, '').split('_').find(id => id !== String(meUserId));
              const avatarSrc = v.avatarUrl || (peerId ? friendsList.find(f => String(f.id) === peerId)?.avatarUrl : null) || null;
              let snippet = '', snippetPrefix = '';
              if (lastMsg) {
                const isMe = String(lastMsg.authorId) === String(meUserId);
                snippetPrefix = isMe ? 'You' : (lastMsg.authorName || '');
                if (lastMsg.kind === 'call_event') { snippet = 'Call ended'; snippetPrefix = ''; }
                else if (lastMsg.attachments?.length) { snippet = 'Attachment'; }
                else { snippet = lastMsg.content || ''; }
              }
              let timeLabel = '';
              if (lastMsg?.createdAt) {
                const d = new Date(lastMsg.createdAt), diffMs = Date.now() - d.getTime();
                const m = Math.floor(diffMs / 60000), h = Math.floor(m / 60), dd = Math.floor(h / 24);
                if (m < 1) timeLabel = 'now'; else if (m < 60) timeLabel = `${m}m`; else if (h < 24) timeLabel = `${h}h`; else if (dd < 7) timeLabel = `${dd}d`; else timeLabel = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              }
              return (
                <button
                  key={v.id}
                  className={`group w-full flex items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors ${isActive ? 'bg-white/10' : 'hover:bg-white/5'}`}
                  onClick={() => onOpenDm(v.id)}
                >
                  <div className="relative shrink-0 h-9 w-9 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                    {avatarSrc ? <ThumbImg w={56} src={avatarSrc} alt={displayName} className="h-full w-full object-cover" /> : <span className="text-xs font-medium text-fg-2">{displayName.slice(0,2).toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-1">
                      <span className={`text-xs font-medium truncate ${unreadCount > 0 || hasNotif ? 'text-white' : 'text-fg-2'}`}>{displayName}</span>
                      {timeLabel && <span className="text-meta text-fg-3 shrink-0">{timeLabel}</span>}
                    </div>
                    {(snippet || snippetPrefix) && (
                      <p className={`text-meta truncate leading-tight ${unreadCount > 0 || hasNotif ? 'text-fg-2' : 'text-fg-3'}`}>
                        {snippetPrefix ? <span>{snippetPrefix}: </span> : null}{snippet}
                      </p>
                    )}
                  </div>
                  {(unreadCount > 0 || hasNotif) && (
                    <span className={`shrink-0 min-w-[18px] h-4 px-1 rounded-full ${tabBgClass} text-white text-meta font-medium flex items-center justify-center leading-none`}>
                      {unreadCount > 99 ? '99+' : unreadCount || '●'}
                    </span>
                  )}
                </button>
              );
            });
          })()}
        </div>
      </div>
    </aside>
  );
}

export default DmSidebar;
import ThumbImg from "../media/ThumbImg";
